/**
 * NDA agreement text — mirrored verbatim from the backend (`NdaText.ts`) so the
 * inline NDA document preview in the pop-up (NdaDocument component) can render
 * the agreement without a round-trip to the BFF.
 *
 * A snapshot test (`ndaText.snapshot.json` + `ndaText.test.ts`) guards against
 * drift between this file and the backend source of truth. If you change the
 * NDA text, update both files + the snapshot, then re-run `npm run build:backend`
 * to regenerate the snapshot from the backend dist.
 *
 * British English. Open Government Licence v3.0.
 */

export interface DiscloseeDetails {
  firstName: string;
  lastName: string;
  addr1: string;
  addr2?: string;
  town: string;
  county?: string;
  postcode: string;
  country: string;
}

/** Format the disclosee's full name. */
export function fullName(d: DiscloseeDetails): string {
  return `${d.firstName} ${d.lastName}`.trim();
}

/** Format the disclosee's address as a single string. */
export function fullAddress(d: DiscloseeDetails): string {
  const parts = [d.addr1, d.addr2, d.town, d.county, d.postcode, d.country].filter(Boolean);
  return parts.join(', ');
}

/** The nine clauses, extracted verbatim from the prototype. */
export const CLAUSES: string[] = [
  '1. The Discloser intends to disclose information (the Confidential Information) to the Recipient for the purpose of the Recipient providing the Discloser with help relating to the development and commercialisation of the Discloser’s idea (the Purpose).',
  '2. The Recipient undertakes not to use the Confidential Information for any purpose except the Purpose, without first obtaining the written agreement of the Discloser.',
  '3. The Recipient undertakes to keep the Confidential Information secure and not to disclose it to any third party except to its employees and professional advisers who need to know the same for the Purpose, who know they owe a duty of confidence to the Discloser and who are bound by obligations equivalent to those in clause 2 above and this clause 3.',
  '4. The undertakings in clauses 2 and 3 above apply to all of the information disclosed by the Discloser to the Recipient, regardless of the way or form in which it is disclosed or recorded but they do not apply to: a) any information which is or in future comes into the public domain (unless as a result of the breach of this Agreement); or b) any information which is already known to the Recipient and which was not subject to any obligation of confidence before it was disclosed to the Recipient by the Discloser.',
  '5. Nothing in this Agreement will prevent the Recipient from making any disclosure of the Confidential Information required by law or by any competent authority.',
  '6. The Recipient will, on written request from the Discloser, return all copies and records of the Confidential Information supplied by the Discloser.',
  '7. Neither this Agreement nor the supply of any information grants the Recipient any licence, interest or right in respect of any intellectual property rights of the Discloser except the right to copy the Confidential Information solely for the Purpose.',
  '8. The undertakings in this Agreement will continue in force for 15 years from the date of this Agreement.',
  '9. This Agreement is governed by, and is to be construed in accordance with, English law. The English Courts will have non-exclusive jurisdiction to deal with any dispute which has arisen or may arise out of, or in connection with, this Agreement.',
];

export const PARTIES_RECIPIENT =
  'INNOVATE PRODUCT DESIGN LIMITED, a company registered in England under company number 06874129 whose registered office is at 36 Endless Street, Salisbury, Wiltshire, England, SP1 3UH (the Recipient)';

export const SIGNATORY = 'Alastair Swanwick';
export const SIGNATORY_ROLE = 'Director, Innovate Product Design Limited';

export const FOOTER =
  'This document is based on a UK Government template under the Open Government Licence v3.0';